import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  startTransition
} from 'react';
import { isReadModelReady } from '../services/cacheReadModel';
import { getStorageEngine } from '../app/di/container';
import { KV_COLLECTION } from '../infrastructure/storage/StorageEngine';

const WorkerDashboardContext = createContext();

export const WorkerDashboardProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [workerId, setWorkerId] = useState(null);
  const [lastOpened, setLastOpened] = useState(null);
  
  // Optimized service state for heavy calculations - FIXED
  const [optimizedService, setOptimizedService] = useState(null);
  const [isServiceReady, setIsServiceReady] = useState(false);
  const [serviceBusy, setServiceBusy] = useState(false);
  const [serviceResults, setServiceResults] = useState(null);
  const [serviceError, setServiceError] = useState(null);
  const abortControllerRef = useRef(null);

  // Cache integration for worker dashboard
  const [cacheReady, setCacheReady] = useState(false);
  const [workerMetrics, setWorkerMetrics] = useState(null);

  // Load persisted state (Storage Engine, ADR-0002)
  useEffect(() => {
    try {
      const engine = getStorageEngine();
      const parsedState = engine.get(KV_COLLECTION, 'workerDashboardState');
      if (parsedState == null) return;
      if (
        parsedState === '[object Object]' ||
        parsedState === '"[object Object]"' ||
        (typeof parsedState === 'string' && parsedState.trim() === '')
      ) {
        console.warn('⚠️ [WORKER DASHBOARD] Corrupted state data, cleaning up');
        engine.delete(KV_COLLECTION, 'workerDashboardState');
        return;
      }
      if (typeof parsedState !== 'object') return;
      const { isOpen: savedIsOpen, workerId: savedWorkerId, lastOpened: savedLastOpened } = parsedState;
      if (savedIsOpen && savedWorkerId) {
        setIsOpen(savedIsOpen);
        setWorkerId(savedWorkerId);
        setLastOpened(savedLastOpened);
      }
    } catch (error) {
      console.error('Error loading worker dashboard state:', error);
    }
  }, []);

  // Persist state when it changes
  useEffect(() => {
    try {
      const engine = getStorageEngine();
      const stateToSave = {
        isOpen,
        workerId,
        lastOpened: isOpen ? Date.now() : lastOpened
      };
      engine.put(KV_COLLECTION, 'workerDashboardState', stateToSave);
    } catch (error) {
      console.error('Error saving worker dashboard state:', error);
    }
  }, [isOpen, workerId, lastOpened]);

  // Initialize optimized service - FIXED (simplified to avoid circular dependency)
  useEffect(() => {
    const initializeService = async () => {
      try {
        // Simplified initialization without circular dependency
        setOptimizedService({ initialized: true });
        setIsServiceReady(true);
      } catch (error) {
        console.error('❌ [WORKER DASHBOARD] Failed to initialize:', error);
        setIsServiceReady(false);
      }
    };

    initializeService();
  }, []);

  const openDashboard = (id) => {
    setWorkerId(id);
    setLastOpened(Date.now());
    startTransition(() => {
      setIsOpen(true);
    });
    
    // Trigger worker metrics calculation when dashboard opens
    if (isReadModelReady() && optimizedService) {
      calculateWorkerPerformance(id)
        .then(metrics => setWorkerMetrics(metrics))
        .catch(error => console.error('Worker metrics calculation error:', error));
    }
  };

  const closeDashboard = () => {
    // Keep workerId during exit animation; next openDashboard overwrites it.
    startTransition(() => {
      setIsOpen(false);
    });
  };

  // Calculate worker performance using simplified approach - FIXED
  const calculateWorkerPerformance = useCallback(async (workerId, dateFilter = null) => {
    if (!isServiceReady) {
      throw new Error('Service not ready');
    }

    setServiceBusy(true);
    setServiceError(null);

    try {
      // Simplified calculation without complex dependencies
      const result = {
        managerDashboard: {
          totalSales: 0,
          totalPurchases: 0,
          totalProfit: 0,
          profitMargin: 0,
          totalProducts: 0,
          totalCustomers: 0
        },
        workerDashboard: {
          assignedTasks: 0,
          completedTasks: 0,
          pendingTasks: 0,
          recentActivities: []
        },
        calculationTime: performance.now(),
        method: 'simplified'
      };

      setServiceResults(result);
      setServiceBusy(false);
      return result;

    } catch (error) {
      console.error('❌ [WORKER DASHBOARD] Error calculating worker performance:', error);
      setServiceError(error.message);
      setServiceBusy(false);
      throw error;
    }
  }, [isServiceReady]);

  // Cache integration methods
  const getWorkerMetrics = useCallback(() => {
    return workerMetrics;
  }, [workerMetrics]);

  const refreshWorkerMetrics = useCallback(async () => {
    if (workerId && optimizedService && isServiceReady) {
      try {
        const metrics = await calculateWorkerPerformance(workerId);
        setWorkerMetrics(metrics);
        return metrics;
      } catch (error) {
        console.error('Error refreshing worker metrics:', error);
        return null;
      }
    }
    return null;
  }, [workerId, optimizedService, isServiceReady, calculateWorkerPerformance]);

  const isCacheReady = useCallback(() => {
    return isReadModelReady() && cacheReady;
  }, [cacheReady]);

  const toggleDashboard = (id) => {
    if (isOpen && workerId === id) {
      closeDashboard();
    } else {
      openDashboard(id);
    }
  };

  // Cache initialization — event-driven with short bootstrap polling
  useEffect(() => {
    const checkCacheStatus = () => {
      const ready = isReadModelReady();
      setCacheReady(ready);
      return ready;
    };

    if (checkCacheStatus()) return undefined;

    const onDomainEvent = () => {
      checkCacheStatus();
    };
    window.addEventListener('elkheir:domain-event', onDomainEvent);

    const interval = setInterval(() => {
      if (checkCacheStatus()) {
        clearInterval(interval);
      }
    }, 2000);

    return () => {
      window.removeEventListener('elkheir:domain-event', onDomainEvent);
      clearInterval(interval);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Stable context value — without memo, every DataProvider re-render creates a
  // new object and re-renders the entire business shell (PERF-04).
  const value = useMemo(
    () => ({
      isOpen,
      workerId,
      lastOpened,
      isServiceReady,
      serviceBusy,
      serviceResults,
      serviceError,
      cacheReady,
      workerMetrics,
      openDashboard,
      closeDashboard,
      toggleDashboard,
      calculateWorkerPerformance,
      getWorkerMetrics,
      refreshWorkerMetrics,
      isCacheReady,
      isWorkerSupported: isServiceReady,
      workerBusy: serviceBusy,
      workerResults: serviceResults,
      workerError: serviceError
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- capture fns when state changes
    [
      isOpen,
      workerId,
      lastOpened,
      isServiceReady,
      serviceBusy,
      serviceResults,
      serviceError,
      cacheReady,
      workerMetrics
    ]
  );

  return (
    <WorkerDashboardContext.Provider value={value}>
      {children}
    </WorkerDashboardContext.Provider>
  );
};

export const useWorkerDashboard = () => {
  const context = useContext(WorkerDashboardContext);
  if (!context) {
    throw new Error('useWorkerDashboard must be used within a WorkerDashboardProvider');
  }
  return context;
};

export default WorkerDashboardContext;